import { useState, useRef, useCallback, useEffect, memo, useMemo } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Utensils, Stethoscope, ShoppingBag } from 'lucide-react';

const sectorData = {
  restaurant: {
    beforeTitle: 'Le Bistrot — Sans automatisation',
    beforeSub: 'Chaque heure est une bataille',
    afterTitle: 'Le Bistrot — Avec FastTask',
    afterSub: 'Tout est géré en arrière-plan',
    before: [
      { time: '08h', title: '12 appels manqués', desc: 'Clients sans réponse. Tables perdues.', val: 60, chip: 'Clients perdus' },
      { time: '09h30', title: 'Saisie manuelle', desc: '45 min de saisie. Erreurs incluses.', val: 75, chip: 'Perte de temps' },
      { time: '12h', title: 'Chaos service midi', desc: 'Double réservation. Client furieux.', val: 95, chip: 'Surbooking' },
      { time: '14h30', title: '3 no-shows', desc: 'Aucun rappel. 180€ de perte.', val: 85, chip: '−180€' },
      { time: '19h', title: 'Téléphone coupé', desc: 'Opportunités manquées après fermeture.', val: 70, chip: 'Perdus' },
    ],
    after: [
      { time: '08h', title: '14 réservations confirmées', desc: "L'IA gère les réservations la nuit.", val: 95, chip: 'Auto' },
      { time: '09h30', title: 'Agenda synchronisé', desc: 'Zéro saisie. Plan de salle prêt.', val: 100, chip: '0 erreur' },
      { time: '12h', title: 'Service fluide', desc: 'Tables bien attribuées, zéro conflit.', val: 100, chip: 'Fluide' },
      { time: '14h30', title: 'Rappels auto envoyés', desc: 'No-shows réduits de 60%.', val: 90, chip: '+60%' },
      { time: '19h', title: "L'IA travaille 24h/24", desc: '8 réservations pour demain.', val: 100, chip: '24/7' },
    ],
    beforeStats: [{ num: '3h', lbl: 'perdues/jour' }, { num: '4', lbl: 'clients perdus' }, { num: '−180€', lbl: 'manque à gagner' }],
    afterStats: [{ num: '3h', lbl: 'récupérées' }, { num: '0', lbl: 'appel manqué' }, { num: '+180€', lbl: 'revenu sauvé' }],
  },
  medical: {
    beforeTitle: 'Dr. Martin — Sans automatisation',
    beforeSub: "L'administratif écrase le temps de soin",
    afterTitle: 'Dr. Martin — Avec FastTask',
    afterSub: 'Concentré sur ses patients',
    before: [
      { time: '08h', title: '23 appels en 1h', desc: "Le personnel ne gère plus la salle d'attente.", val: 80, chip: 'Surcharge' },
      { time: '09h30', title: 'Agenda manuel', desc: 'Erreurs de saisie. RDV en double.', val: 70, chip: 'Erreurs' },
      { time: '11h', title: '2 no-shows', desc: 'Créneaux vides, médecin qui attend.', val: 75, chip: 'No-shows' },
      { time: '14h', title: 'Retard de 40 min', desc: "No-shows et erreurs ont tout décalé.", val: 90, chip: 'Retard' },
      { time: '18h30', title: 'Épuisement total', desc: "3h d'administratif. Burn-out.", val: 95, chip: 'Burn-out' },
    ],
    after: [
      { time: '08h', title: 'Agenda prêt', desc: "L'IA a géré tous les RDV cette nuit.", val: 100, chip: 'Auto' },
      { time: '09h30', title: 'Rappels automatiques', desc: 'Rappel WhatsApp. Zéro no-show.', val: 95, chip: '−60%' },
      { time: '11h', title: 'Zéro interruption', desc: "Le personnel gère la salle, l'IA le reste.", val: 100, chip: 'Focus' },
      { time: '14h', title: 'Planning respecté', desc: 'Pas de retard. Patients satisfaits.', val: 100, chip: 'Ponctuel' },
      { time: '18h30', title: 'Journée sereine', desc: "Demain déjà organisé. 2h de gagnées.", val: 100, chip: '+2h' },
    ],
    beforeStats: [{ num: '3h', lbl: 'admin/jour' }, { num: '23', lbl: 'appels/matin' }, { num: '4', lbl: 'no-shows/sem' }],
    afterStats: [{ num: '3h', lbl: 'soins gagnés' }, { num: '0', lbl: 'appel manqué' }, { num: '−60%', lbl: 'no-shows' }],
  },
  retail: {
    beforeTitle: 'ModaStyle — Sans automatisation',
    beforeSub: 'L\'équipe répond aux mêmes questions en boucle',
    afterTitle: 'ModaStyle — Avec FastTask',
    afterSub: "L'IA gère le SAV, l'équipe vend",
    before: [
      { time: '09h', title: '47 messages ignorés', desc: 'Tailles, délais, retours. Clients impatients.', val: 70, chip: 'Non traités' },
      { time: '10h30', title: '12 paniers abandonnés', desc: 'Aucune relance. Clients chez la concurrence.', val: 65, chip: '−340€' },
      { time: '13h', title: 'Rupture de stock', desc: 'Best-seller épuisé. Commandes refusées.', val: 85, chip: 'Rupture' },
      { time: '16h', title: 'Avis négatif', desc: 'Pas de réponse depuis 6h. Avis Google 1★.', val: 90, chip: '1★' },
      { time: '20h', title: 'Boutique fermée', desc: '18 messages sans réponse.', val: 60, chip: 'Perdus' },
    ],
    after: [
      { time: '09h', title: '47 messages traités', desc: "L'IA a répondu cette nuit. Clients satisfaits.", val: 100, chip: '<2s' },
      { time: '10h30', title: 'Relances auto', desc: '12 paniers relancés. 8 convertis.', val: 85, chip: '+8 ventes' },
      { time: '13h', title: 'Alerte stock à 7h', desc: "Risque de rupture détecté. Réassort commandé.", val: 100, chip: 'Proactif' },
      { time: '16h', title: '0 message en attente', desc: 'Réponse instantanée. Satisfaction max.', val: 100, chip: '+25%' },
      { time: '20h', title: 'Ouvert 24h/24', desc: "L'IA vend même à minuit.", val: 100, chip: '24/7' },
    ],
    beforeStats: [{ num: '47', lbl: 'msg ignorés' }, { num: '−340€', lbl: 'paniers perdus' }, { num: '6h', lbl: 'délai réponse' }],
    afterStats: [{ num: '0', lbl: 'msg en attente' }, { num: '+8', lbl: 'ventes relancées' }, { num: '<2s', lbl: 'délai réponse' }],
  },
};

const sectors = [
  { id: 'restaurant', label: 'Restauration', Icon: Utensils },
  { id: 'medical', label: 'Médical', Icon: Stethoscope },
  { id: 'retail', label: 'Retail', Icon: ShoppingBag },
];

const TimelineItem = memo(function TimelineItem({ item, isBefore }) {
  return (
    <div className={`ba-row ${isBefore ? 'ba-row--before' : 'ba-row--after'}`}>
      <div className="ba-row-time">{item.time}</div>
      <div className="ba-row-body">
        <div className="ba-row-head">
          <span className="ba-row-title">{item.title}</span>
          <span className={`ba-tag ${isBefore ? 'ba-tag--red' : 'ba-tag--green'}`}>{item.chip}</span>
        </div>
        <div className="ba-row-desc">{item.desc}</div>
        <div className="ba-meter">
          <div
            className={`ba-meter-fill ${isBefore ? 'ba-meter-fill--red' : 'ba-meter-fill--green'}`}
            style={{ width: `${item.val}%` }}
          />
        </div>
      </div>
    </div>
  );
});

const StatBlock = memo(function StatBlock({ stats, variant }) {
  return (
    <div className={`ba-stats ${variant === 'after' ? 'ba-stats--after' : ''}`}>
      {stats.map((s, i) => (
        <div key={i} className="ba-stat">
          <div className={`ba-stat-num ${variant === 'before' ? 'ba-stat-num--red' : 'ba-stat-num--green'}`}>{s.num}</div>
          <div className="ba-stat-lbl">{s.lbl}</div>
        </div>
      ))}
    </div>
  );
});

// Direct DOM manipulation for the slider — zero React re-renders while dragging
function useSlider(splitRef, afterRef, dividerRef) {
  const pct = useRef(50);
  const dragging = useRef(false);
  const raf = useRef(0);
  const hasAutoPlayed = useRef(false);

  const applyPosition = useCallback((p) => {
    pct.current = Math.max(0, Math.min(100, p));
    if (afterRef.current) afterRef.current.style.clipPath = `inset(0 0 0 ${pct.current}%)`;
    if (dividerRef.current) dividerRef.current.style.left = `${pct.current}%`;
  }, [afterRef, dividerRef]);

  const onMove = useCallback((e) => {
    if (!dragging.current || !splitRef.current) return;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const rect = splitRef.current.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      applyPosition(((clientX - rect.left) / rect.width) * 100);
    });
  }, [splitRef, applyPosition]);

  const onDown = useCallback((e) => {
    dragging.current = true;
    if (e.type === 'mousedown') e.preventDefault();
  }, []);

  const onUp = useCallback(() => { dragging.current = false; }, []);

  // Bind global listeners
  useEffect(() => {
    const opts = { passive: true };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, opts);
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      cancelAnimationFrame(raf.current);
    };
  }, [onMove, onUp]);

  // Auto-animate on viewport entry using rAF
  useEffect(() => {
    const el = splitRef.current;
    if (!el || hasAutoPlayed.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || hasAutoPlayed.current) return;
      hasAutoPlayed.current = true;
      obs.disconnect();
      let pos = 50, phase = 0, start = 0;
      const duration = [900, 1200, 600]; // left sweep, right sweep, settle
      const targets = [8, 92, 50];
      const step = (ts) => {
        if (!start) start = ts;
        const elapsed = ts - start;
        const t = Math.min(elapsed / duration[phase], 1);
        // ease-out cubic
        const ease = 1 - Math.pow(1 - t, 3);
        const from = phase === 0 ? 50 : targets[phase - 1];
        pos = from + (targets[phase] - from) * ease;
        applyPosition(pos);
        if (t >= 1) {
          phase++;
          start = 0;
          if (phase >= targets.length) return;
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [splitRef, applyPosition]);

  const reset = useCallback(() => applyPosition(50), [applyPosition]);

  return { onDown, reset };
}

export default function BeforeAfterSection() {
  const titleRef = useScrollReveal();
  const wrapRef = useScrollReveal();
  const [sector, setSector] = useState('restaurant');
  const splitRef = useRef(null);
  const afterRef = useRef(null);
  const dividerRef = useRef(null);

  const { onDown, reset } = useSlider(splitRef, afterRef, dividerRef);

  const data = useMemo(() => sectorData[sector], [sector]);

  const switchSector = useCallback((id) => {
    setSector(id);
    // reset slider after React flushes the new content
    requestAnimationFrame(() => reset());
  }, [reset]);

  return (
    <section className="ba-section py-20 md:py-28">
      <div className="container mx-auto px-6 md:px-12 max-w-6xl">

        {/* Header */}
        <div ref={titleRef} className="scroll-reveal text-center mb-16 md:mb-20">
          <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)] mb-5">
            Comparaison interactive
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-[3.25rem] font-semibold tracking-tight leading-[1.1] text-foreground mb-5">
            Une journée <span className="text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]">sans</span> vs <span className="text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]">avec</span> FastTask
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-lg mx-auto leading-relaxed">
            Glissez pour voir la différence que fait l'automatisation IA au quotidien.
          </p>
        </div>

        {/* Sector tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-14">
          {sectors.map((s) => {
            const active = sector === s.id;
            return (
              <button
                key={s.id}
                onClick={() => switchSector(s.id)}
                className={`ba-tab ${active ? 'ba-tab--on' : ''}`}
              >
                <s.Icon className="ba-tab-icon" />
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Hint */}
        <p className="ba-hint">
          <span className="ba-hint-arrow">‹</span>
          Glissez pour comparer
          <span className="ba-hint-arrow ba-hint-arrow--r">›</span>
        </p>

        {/* Slider */}
        <div
          ref={(el) => { splitRef.current = el; if (wrapRef.current !== el) wrapRef.current = el; }}
          className="scroll-reveal ba-frame"
          onMouseDown={onDown}
          onTouchStart={onDown}
        >
          {/* Before */}
          <div className="ba-pane ba-pane--dark">
            <span className="ba-badge ba-badge--red">Sans FastTask</span>
            <div className="ba-pane-head">
              <h3 className="ba-pane-title">{data.beforeTitle}</h3>
              <p className="ba-pane-sub">{data.beforeSub}</p>
            </div>
            <div className="ba-list">
              {data.before.map((item, i) => <TimelineItem key={i} item={item} isBefore />)}
            </div>
            <StatBlock stats={data.beforeStats} variant="before" />
          </div>

          {/* After */}
          <div ref={afterRef} className="ba-pane ba-pane--light" style={{ clipPath: 'inset(0 0 0 50%)' }}>
            <span className="ba-badge ba-badge--green">Avec FastTask</span>
            <div className="ba-pane-head ba-pane-head--light">
              <h3 className="ba-pane-title ba-pane-title--dark">{data.afterTitle}</h3>
              <p className="ba-pane-sub ba-pane-sub--dark">{data.afterSub}</p>
            </div>
            <div className="ba-list">
              {data.after.map((item, i) => <TimelineItem key={i} item={item} isBefore={false} />)}
            </div>
            <StatBlock stats={data.afterStats} variant="after" />
          </div>

          {/* Divider */}
          <div ref={dividerRef} className="ba-line" style={{ left: '50%' }}>
            <div className="ba-knob">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7 5l-4 5 4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M13 5l4 5-4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
